import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Drawing } from './types';
import { getDrawingNoteHtml, noteHtmlToPlainText } from './noteHtml';

type NoteLayout = {
  drawing: Drawing;
  anchorX: number;
  anchorY: number;
  boxX: number;
  boxY: number;
  visible: boolean;
};

type Props = {
  notes: NoteLayout[];
  width: number;
  height: number;
  selectedId: string | null;
  editingId: string | null;
  onSelect: (id: string) => void;
  onBeginEdit: (id: string) => void;
  onCommitEdit: (id: string, text: string) => void;
  onCancelEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDragOffset: (id: string, dx: number, dy: number, commit: boolean) => void;
  onStyleChange: (id: string, nextStyle: Partial<NonNullable<Drawing['style']>>) => void;
};

// font-size dropdown; we intentionally restrict the user to a reasonable range
// per recent user request only 12–20px is offered. export for unit tests.
export const FONT_SIZES = Array.from({ length: 20 - 12 + 1 }, (_, i) => 12 + i);
const COLORS = ['#FFFFFF', '#FACC15', '#EF4444', '#3B82F6', '#22C55E', '#EC4899'];
const NOTE_MAX_WIDTH = 360;

function nearestPointOnBoxEdge(anchorX: number, anchorY: number, boxX: number, boxY: number, boxW: number, boxH: number) {
  const left = boxX;
  const right = boxX + boxW;
  const top = boxY;
  const bottom = boxY + boxH;

  const candidates = [
    { x: Math.max(left, Math.min(right, anchorX)), y: top },
    { x: Math.max(left, Math.min(right, anchorX)), y: bottom },
    { x: left, y: Math.max(top, Math.min(bottom, anchorY)) },
    { x: right, y: Math.max(top, Math.min(bottom, anchorY)) },
  ];

  let best = candidates[0];
  let bestDist = Number.POSITIVE_INFINITY;
  for (const point of candidates) {
    const d = Math.hypot(anchorX - point.x, anchorY - point.y);
    if (d < bestDist) {
      bestDist = d;
      best = point;
    }
  }
  return best;
}

export default function NoteOverlayLayer({
  notes,
  width,
  height,
  selectedId,
  editingId,
  onSelect,
  onBeginEdit,
  onCommitEdit,
  onCancelEdit,
  onDelete,
  onDragOffset,
  onStyleChange,
}: Props) {
  const [draftText, setDraftText] = useState('');
  const [activeFontSize, setActiveFontSize] = useState(14);
  const [activeFontColor, setActiveFontColor] = useState('#E5E7EB');
  const activeEditing = useMemo(
    () => notes.find((n) => n.drawing.id === editingId) ?? null,
    [notes, editingId],
  );
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!activeEditing) return;
    const html = getDrawingNoteHtml(activeEditing.drawing);
    setDraftText(html);
    setActiveFontSize(Number(activeEditing.drawing.style?.fontSize ?? 14));
    setActiveFontColor(String(activeEditing.drawing.style?.fontColor || '#E5E7EB'));
    if (editorRef.current) {
      editorRef.current.innerHTML = html;
    }
  }, [activeEditing?.drawing.id]);

  useEffect(() => {
    if (!activeEditing || !editorRef.current) return;
    const node = editorRef.current;
    node.focus();
    try {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        const range = document.createRange();
        range.selectNodeContents(node);
        range.collapse(false);
        sel.addRange(range);
      }
    } catch {}
  }, [activeEditing?.drawing.id]);

  const commitActive = () => {
    if (!activeEditing) return;
    const html = editorRef.current ? String(editorRef.current.innerHTML || '') : draftText;
    onCommitEdit(activeEditing.drawing.id, html);
  };

  const cancelActive = () => {
    if (!activeEditing) return;
    onCancelEdit(activeEditing.drawing.id);
  };

  const applyStyleToSelection = (opts: { fontSize?: number; fontColor?: string }) => {
    try {
      if (opts.fontSize) setActiveFontSize(opts.fontSize);
      if (opts.fontColor) setActiveFontColor(opts.fontColor);

      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (range.collapsed) {
        const span = document.createElement('span');
        if (opts.fontSize) span.style.fontSize = `${opts.fontSize}px`;
        if (opts.fontColor) span.style.color = opts.fontColor;
        const zwsp = document.createTextNode('\u200B');
        span.appendChild(zwsp);
        range.insertNode(span);

        const after = document.createRange();
        after.setStart(zwsp, 1);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
        setDraftText(editorRef.current ? String(editorRef.current.innerHTML || '') : draftText);
        return;
      }

      const span = document.createElement('span');
      if (opts.fontSize) span.style.fontSize = `${opts.fontSize}px`;
      if (opts.fontColor) span.style.color = opts.fontColor;

      const frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);

      // reselect the newly inserted span
      sel.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      sel.addRange(newRange);

      setDraftText(editorRef.current ? String(editorRef.current.innerHTML || '') : draftText);
    } catch {}
  };

  const insertLineBreakAtCaret = () => {
    try {
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const range = sel.getRangeAt(0);
      range.deleteContents();

      const br = document.createElement('br');
      range.insertNode(br);

      const nextRange = document.createRange();
      nextRange.setStartAfter(br);
      nextRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(nextRange);

      setDraftText(String(editor.innerHTML || ''));
    } catch {}
  };

  return (
    <div className="absolute inset-0 z-[27] pointer-events-none" data-drawing-note-ui="true">
      <svg className="absolute inset-0 pointer-events-none" width={Math.max(1, width)} height={Math.max(1, height)}>
        {notes.filter((n) => n.visible).map((note) => {
          const anchors = note.drawing.anchors as any;
          const style = note.drawing.style || {};
          const fontColor = String(style.fontColor || '#E5E7EB');
          const lineOpacity = 0.7;
          const pad = Number(style.padding ?? 8);
          const fontSize = Number(style.fontSize ?? 14);
          const plainText = noteHtmlToPlainText(getDrawingNoteHtml(note.drawing));
          const longestLine = plainText.split('\n').reduce((max, line) => Math.max(max, line.length), 0);
          const fallbackW = Math.max(70, Math.min(NOTE_MAX_WIDTH, longestLine * Math.max(6, fontSize * 0.55) + pad * 2));
          const fallbackH = Math.max(36, Math.min(220, Math.ceil(Math.max(1, plainText.length) / Math.max(12, Math.floor((fallbackW - pad * 2) / Math.max(6, fontSize * 0.55)))) * (fontSize + 6) + pad * 2));
          const boxW = Number(anchors?.box?.w ?? fallbackW);
          const boxH = Number(anchors?.box?.h ?? fallbackH);
          const point = nearestPointOnBoxEdge(note.anchorX, note.anchorY, note.boxX, note.boxY, boxW, boxH);
          return (
            <g key={`line-${note.drawing.id}`}>
              <line
                x1={note.anchorX}
                y1={note.anchorY}
                x2={point.x}
                y2={point.y}
                stroke={fontColor}
                strokeOpacity={lineOpacity}
                strokeWidth={1.5}
              />
              <circle cx={note.anchorX} cy={note.anchorY} r={3} fill={fontColor} fillOpacity={0.9} />
            </g>
          );
        })}
      </svg>

      {notes.map((note) => {
        if (!note.visible) return null;
        const drawing = note.drawing;
        const style = drawing.style || {};
        const anchors = drawing.anchors as any;
        const fontSize = Number(style.fontSize ?? 14);
        const isSelected = selectedId === drawing.id;
        const isEditing = editingId === drawing.id;
        const fontColor = isEditing
          ? String(activeFontColor || style.fontColor || '#E5E7EB')
          : String(style.fontColor || '#E5E7EB');
        const bgColor = style.bgColor === 'transparent' ? 'transparent' : String(style.bgColor || '#222226');
        const pad = Number(style.padding ?? 8);
        const textHtml = getDrawingNoteHtml(drawing);
        const plainText = noteHtmlToPlainText(textHtml);
        const longestLine = plainText.split('\n').reduce((max, line) => Math.max(max, line.length), 0);
        const fallbackW = Math.max(70, Math.min(NOTE_MAX_WIDTH, longestLine * Math.max(6, fontSize * 0.55) + pad * 2));
        const fallbackH = Math.max(36, Math.min(220, Math.ceil(Math.max(1, plainText.length) / Math.max(12, Math.floor((fallbackW - pad * 2) / Math.max(6, fontSize * 0.55)))) * (fontSize + 6) + pad * 2));
        const boxH = Number(anchors?.box?.h ?? fallbackH);

        return (
          <React.Fragment key={drawing.id}>
            {(isSelected || isEditing) && (
              <div
                className="absolute pointer-events-auto flex items-center gap-1 rounded border border-gray-600 bg-[#111827]/95 p-1"
                style={{ left: note.boxX, top: note.boxY - 38 }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <select
                  className="h-6 rounded border border-gray-600 bg-[#1F2937] px-1 text-[11px] text-gray-100"
                  value={isEditing ? activeFontSize : fontSize}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const next = Number.parseInt(e.target.value, 10) || 14;
                    if (isEditing) {
                      applyStyleToSelection({ fontSize: next });
                    } else {
                      onStyleChange(drawing.id, { fontSize: next });
                    }
                  }}
                >
                  {FONT_SIZES.map((s) => (
                    <option key={s} value={s}>{`${s}px`}</option>
                  ))}
                </select>

                <div className="flex items-center gap-1">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="h-4 w-4 rounded border border-black/30"
                      style={{ backgroundColor: color, outline: (isEditing ? activeFontColor : fontColor) === color ? '1px solid #fff' : 'none' }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => {
                        if (isEditing) {
                          applyStyleToSelection({ fontColor: color });
                          onStyleChange(drawing.id, { fontColor: color, lineColor: color });
                        } else {
                          onStyleChange(drawing.id, { fontColor: color, lineColor: color });
                        }
                      }}
                      title={color}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="h-6 rounded border border-red-700 bg-red-900/70 px-2 text-[11px] text-red-200"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // call delete early so blur from editor doesn't unmount this button first
                    onDelete(drawing.id);
                  }}
                  title="Delete note"
                >
                  Delete
                </button>
              </div>
            )}

            <div
              className="absolute pointer-events-auto"
              style={{
                left: note.boxX,
                top: note.boxY,
                display: 'inline-block',
                width: 'fit-content',
                maxWidth: NOTE_MAX_WIDTH,
                minWidth: 56,
                minHeight: boxH,
                color: fontColor,
                fontSize,
                lineHeight: 1.25,
                background: bgColor,
                border: `1px solid ${fontColor}`,
                borderRadius: 8,
                boxShadow: isSelected ? '0 0 0 1px rgba(245,158,11,0.4)' : 'none',
                padding: pad,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                overflowX: 'hidden',
                userSelect: isEditing ? 'text' : 'none',
                cursor: isEditing ? 'text' : 'move',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                onSelect(drawing.id);
                if (isEditing) return;

                const startX = e.clientX;
                const startY = e.clientY;
                const startDx = Number((drawing.anchors as any)?.dx ?? 12);
                const startDy = Number((drawing.anchors as any)?.dy ?? -12);

                const onMove = (ev: MouseEvent) => {
                  const dx = startDx + (ev.clientX - startX);
                  const dy = startDy + (ev.clientY - startY);
                  onDragOffset(drawing.id, dx, dy, false);
                };

                const onUp = (ev: MouseEvent) => {
                  const dx = startDx + (ev.clientX - startX);
                  const dy = startDy + (ev.clientY - startY);
                  onDragOffset(drawing.id, dx, dy, true);
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                };

                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(drawing.id);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                onBeginEdit(drawing.id);
              }}
            >
              {isEditing ? (
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onKeyDownCapture={(e) => {
                    if (e.key === 'Delete' || e.key === 'Backspace') {
                      e.stopPropagation();
                    }
                  }}
                  onInput={() => setDraftText(editorRef.current ? String(editorRef.current.innerHTML || '') : '')}
                  onBlur={commitActive}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                      e.preventDefault();
                      insertLineBreakAtCaret();
                    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                      e.preventDefault();
                      commitActive();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelActive();
                    }
                  }}
                  style={{
                    display: 'inline-block',
                    width: 'fit-content',
                    maxWidth: NOTE_MAX_WIDTH - pad * 2,
                    minWidth: 40,
                    minHeight: Math.max(32, boxH - pad * 2),
                    color: fontColor,
                    fontSize,
                    lineHeight: 1.25,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    overflowY: 'auto',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                />
              ) : (
                <div
                  style={{
                    display: 'inline-block',
                    width: 'fit-content',
                    maxWidth: NOTE_MAX_WIDTH - pad * 2,
                    minWidth: 1,
                    minHeight: Math.max(32, boxH - pad * 2),
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{ __html: textHtml || ' ' }}
                />
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
